'use client';

import { useState } from 'react';
import { Mail, MessageSquare } from 'lucide-react';
import type { Person, Privacy, ProfileMe, Publication, Subscriptions } from '@/lib/types';
import { useProfile } from './ProfileContext';
import { CampusIcon } from './CampusIcon';
import { setSubscriptions, useSubscriptions } from './subscriptionsStore';
import { Kicker, SwitchRow, useConfirm } from './ui';
import {
  ChannelCard,
  ChannelDot,
  PublicationThumb,
  PublicationTile,
  SubscribedPreview,
} from '@church/nextjs-ui/website/profile';

const BULK_EMAIL_KEY = 'bulk-email';
const TEXTING_KEY = 'texting';

// The titles say it; no line under them (Colton, 2026-09-16).
const DIRECTORY_ROWS: {
  key: keyof Privacy;
  title: (first: string | null) => string;
}[] = [
  { key: 'removeFromDirectory', title: () => 'In the directory' },
  { key: 'emailUnlisted', title: (f) => (f ? `Share ${f}'s email` : 'Share my email') },
  { key: 'mobileUnlisted', title: (f) => (f ? `Share ${f}'s mobile` : 'Share my mobile') },
];

/** Patch one person's Contacts flags in the page's data, me or a member. */
function patchPrivacy(d: ProfileMe, contactId: number, patch: Partial<Privacy>): ProfileMe {
  if (d.me.contactId === contactId)
    return { ...d, me: { ...d.me, privacy: { ...d.me.privacy, ...patch } } };
  return {
    ...d,
    members: d.members.map((m) =>
      m.contactId === contactId ? { ...m, privacy: { ...m.privacy, ...patch } } : m
    ),
  };
}

/**
 * One person's communication flags and the writes behind them, shared by
 * the card and the sheet so a switch flipped in either place agrees with
 * the other. Every write is optimistic: the flag flips at once, the
 * subscriptions cache and the page's data are patched, and a failure puts
 * them back.
 */
function useCommunication(person: Person) {
  const { api, data, run, update } = useProfile();
  const confirm = useConfirm();
  const isMe = person.contactId === data.me.contactId;
  const subs = useSubscriptions(api, person.contactId);
  const [saving, setSaving] = useState<Set<string | number>>(new Set());
  // Read the flags live from the page's data, so the card and the sheet agree.
  const live = isMe
    ? data.me
    : (data.members.find((m) => m.contactId === person.contactId) ?? person);
  const privacy = live.privacy;

  const mark = (key: string | number, on: boolean) =>
    setSaving((s) => {
      const next = new Set(s);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const writePrefs = (
    key: string | number,
    patch: Parameters<typeof api.updateSubscriptions>[1],
    optimistic: (p: Subscriptions) => Subscriptions
  ) => {
    const before = subs;
    if (subs && subs !== 'error') setSubscriptions(person.contactId, optimistic(subs));
    mark(key, true);
    void run(
      async () => {
        const server = await api.updateSubscriptions(person.contactId, patch);
        setSubscriptions(person.contactId, server);
      },
      () => {
        if (before) setSubscriptions(person.contactId, before);
      }
    ).finally(() => mark(key, false));
  };

  const togglePublication = (pub: Publication, subscribed: boolean) =>
    writePrefs(
      pub.publicationId,
      { publications: [{ publicationId: pub.publicationId, subscribed }] },
      (p) => ({
        ...p,
        publications: p.publications.map((x) =>
          x.publicationId === pub.publicationId ? { ...x, subscribed } : x
        ),
      })
    );

  // Both channel switches ask first: one stray tap would otherwise opt a whole
  // person in or out of every list, or out of every text.
  const who = isMe ? 'you' : person.nickname || person.firstName;
  const toggleEmail = async (receiving: boolean) => {
    const ok = await confirm(
      receiving
        ? {
            title: 'Turn on email subscriptions?',
            body: `${isMe ? 'You' : who} will get email from the Woodside lists ${isMe ? 'you are' : 'they are'} subscribed to.`,
            confirmLabel: 'Turn on',
          }
        : {
            title: 'Turn off email subscriptions?',
            body: `${isMe ? 'You' : who} will stop getting newsletters and other email from Woodside's lists. Receipts and confirmations still come.`,
            confirmLabel: 'Turn off',
          }
    );
    if (!ok) return;
    update((d) => patchPrivacy(d, person.contactId, { bulkEmailOptOut: !receiving }));
    writePrefs(BULK_EMAIL_KEY, { bulkEmailOptOut: !receiving }, (p) => ({
      ...p,
      bulkEmailOptOut: !receiving,
    }));
  };
  const toggleTexting = async (optedIn: boolean) => {
    const ok = await confirm(
      optedIn
        ? {
            title: 'Opt into texting?',
            body: `Woodside can text ${isMe ? 'you' : who} at the mobile number on file. Reply STOP any time to opt out.`,
            confirmLabel: 'Opt in',
          }
        : {
            title: 'Opt out of texting?',
            body: `${isMe ? 'You' : who} will stop getting text messages from Woodside.`,
            confirmLabel: 'Opt out',
          }
    );
    if (!ok) return;
    update((d) => patchPrivacy(d, person.contactId, { doNotText: !optedIn }));
    writePrefs(TEXTING_KEY, { textingOptedIn: optedIn }, (p) => ({
      ...p,
      texting: { ...p.texting, optedIn },
    }));
  };

  // The directory flags live on Contacts; the switch reads as "share", so it is the inverse of the flag.
  const toggleDirectory = (key: keyof Privacy, share: boolean) => {
    const value = !share;
    const before = privacy;
    update((d) => patchPrivacy(d, person.contactId, { [key]: value }));
    mark(key, true);
    void run(
      () => api.updatePrivacy(person.contactId, { [key]: value }),
      () => update((d) => patchPrivacy(d, person.contactId, before))
    ).finally(() => mark(key, false));
  };

  const publications =
    subs && subs !== 'error'
      ? [...subs.publications].sort((a, b) => a.sortOrder - b.sortOrder)
      : [];

  return {
    isMe,
    subs,
    publications,
    privacy,
    emailOn: !privacy.bulkEmailOptOut,
    textsOn: !privacy.doNotText,
    saving,
    toggleEmail,
    toggleTexting,
    togglePublication,
    toggleDirectory,
  };
}

/**
 * The Email channel as a real switch and the emails the person actually
 * gets as small previews, a tap on which opens the lists. The sign-in
 * drawer's Communication block.
 */
export function EmailControls({
  person,
  onLists,
}: {
  person: Person;
  /** Open the member sheet on the publication tiles */
  onLists: () => void;
}) {
  const { data } = useProfile();
  const c = useCommunication(person);
  const campusId = data.household?.congregationId ?? data.me.webCongregationId;
  const got = c.publications.filter((p) => p.subscribed);
  return (
    // The switch is a control of its own: flipping it must not open the sheet.
    <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
      <div className="-mx-2">
        <SwitchRow
          title="Email Subscriptions"
          description={
            c.emailOn
              ? c.subs === null
                ? 'Loading your lists…'
                : c.subs === 'error'
                  ? 'Newsletters and updates'
                  : c.publications.length > 0
                    ? `${got.length} of ${c.publications.length} lists`
                    : 'Newsletters and updates'
              : 'Only receipts and confirmations'
          }
          on={c.emailOn}
          saving={c.saving.has(BULK_EMAIL_KEY)}
          onToggle={c.toggleEmail}
          leading={
            <ChannelDot icon={<Mail className="h-4 w-4" strokeWidth={2.5} />} on={c.emailOn} />
          }
        />
      </div>
      {c.emailOn && c.subs !== null && c.subs !== 'error' && c.publications.length > 0 && (
        <SubscribedPreview
          publications={got}
          campusArt={<CampusIcon congregationId={campusId} className="h-7 w-7" />}
          onOpen={onLists}
        />
      )}
    </div>
  );
}

/** The Text channel as a real switch. The sign-in drawer's Communication block. */
export function TextControls({ person }: { person: Person }) {
  const c = useCommunication(person);
  return (
    <div className="-mx-2" onClick={(e) => e.stopPropagation()}>
      <SwitchRow
        title="Opt into texting"
        on={c.textsOn}
        saving={c.saving.has(TEXTING_KEY)}
        onToggle={c.toggleTexting}
        leading={
          <ChannelDot
            icon={<MessageSquare className="h-4 w-4" strokeWidth={2.5} />}
            on={c.textsOn}
          />
        }
      />
    </div>
  );
}

/**
 * The Email channel as one big switch, then the publications as a wall of
 * tiles (only meaning something while Email is on). The sign-in drawer's
 * "Which emails" page.
 */
export function EmailChannel({ person }: { person: Person }) {
  const { data } = useProfile();
  const c = useCommunication(person);
  const first = c.isMe ? null : person.nickname || person.firstName;
  const campusId = data.household?.congregationId ?? data.me.webCongregationId;
  const { subs, publications, emailOn, saving } = c;
  return (
    <div className="flex flex-col gap-4">
      <ChannelCard
        icon={<Mail className="h-6 w-6" strokeWidth={2.5} />}
        title="Email Subscriptions"
        // The one line worth keeping: turning Email Subscriptions off does not stop receipts.
        description="Receipts and confirmations still come either way."
        on={emailOn}
        saving={saving.has(BULK_EMAIL_KEY)}
        onToggle={c.toggleEmail}
      />
      {subs === null ? (
        <div className="grid grid-cols-2 gap-3 @lg:grid-cols-3" aria-busy="true">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-36 animate-pulse rounded-3xl bg-gray-200" />
          ))}
        </div>
      ) : subs === 'error' ? (
        <p className="px-1 text-sm text-gray-500">We couldn't load the subscriptions right now.</p>
      ) : publications.length > 0 ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 px-1">
            <Kicker>{emailOn ? 'Which emails' : 'Which emails (turn Email on first)'}</Kicker>
            {emailOn && (
              <span className="text-xs text-gray-500">
                {publications.filter((p) => p.subscribed).length} of {publications.length}
              </span>
            )}
          </div>
          <div
            className={`grid grid-cols-2 gap-3 transition-opacity @lg:grid-cols-3 ${emailOn ? '' : 'pointer-events-none opacity-40'}`}
          >
            {publications.map((pub) => (
              <PublicationTile
                key={pub.publicationId}
                pub={pub}
                campusArt={<CampusIcon congregationId={campusId} className="h-14 w-14" />}
                saving={saving.has(pub.publicationId)}
                onToggle={(next) => c.togglePublication(pub, next)}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="px-1 text-sm text-gray-500">No subscriptions available right now.</p>
      )}
    </div>
  );
}

/**
 * The Subscriptions card's body, every switch writing straight from the card
 * (no sheet to open first): Email Subscriptions with each list it brings as a
 * row of its own, then texting, then who can find the person in the directory.
 */
export function SubscriptionsControls({ person }: { person: Person }) {
  const { data } = useProfile();
  const c = useCommunication(person);
  const first = c.isMe ? null : person.nickname || person.firstName;
  const campusId = data.household?.congregationId ?? data.me.webCongregationId;
  const got = c.publications.filter((p) => p.subscribed);
  return (
    <div className="flex flex-col gap-3">
      {/* Email: the channel, then the lists while it is on. */}
      <div className="-mx-2 flex flex-col">
        <SwitchRow
          title="Email Subscriptions"
          description={
            c.emailOn
              ? c.publications.length > 0
                ? `${got.length} of ${c.publications.length} lists`
                : 'Newsletters and updates'
              : 'Only receipts and confirmations'
          }
          on={c.emailOn}
          saving={c.saving.has(BULK_EMAIL_KEY)}
          onToggle={c.toggleEmail}
          leading={
            <ChannelDot icon={<Mail className="h-4 w-4" strokeWidth={2.5} />} on={c.emailOn} />
          }
        />
        {c.emailOn &&
          (c.subs === null ? (
            <div className="flex flex-col gap-2 px-4 py-2" aria-busy="true">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="h-11 animate-pulse rounded-xl bg-gray-100" />
              ))}
            </div>
          ) : c.subs === 'error' ? (
            <p className="px-4 py-2 text-sm text-gray-500">We couldn't load the lists right now.</p>
          ) : (
            c.publications.map((pub) => (
              <SwitchRow
                key={pub.publicationId}
                title={pub.title}
                description={pub.description}
                on={pub.subscribed}
                saving={c.saving.has(pub.publicationId)}
                onToggle={(next) => c.togglePublication(pub, next)}
                leading={
                  <PublicationThumb
                    pub={pub}
                    compact
                    campusArt={<CampusIcon congregationId={campusId} className="h-6 w-6" />}
                  />
                }
              />
            ))
          ))}
      </div>
      {/* Texting */}
      <div className="-mx-2 border-t border-gray-100 pt-1">
        <SwitchRow
          title="Opt into texting"
          description={c.textsOn ? 'Texts go to the mobile number on file' : undefined}
          on={c.textsOn}
          saving={c.saving.has(TEXTING_KEY)}
          onToggle={c.toggleTexting}
          leading={
            <ChannelDot
              icon={<MessageSquare className="h-4 w-4" strokeWidth={2.5} />}
              on={c.textsOn}
            />
          }
        />
      </div>
      {/* The directory flags. */}
      <div className="flex flex-col gap-1 border-t border-gray-100 pt-3">
        <Kicker>Church directory</Kicker>
        <div className="-mx-2">
          {DIRECTORY_ROWS.map((r) => (
            <SwitchRow
              key={r.key}
              title={r.title(first)}
              on={!c.privacy[r.key]}
              saving={c.saving.has(r.key)}
              onToggle={(next) => c.toggleDirectory(r.key, next)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
